import React from 'react';
import PropTypes from 'prop-types';
import cx from 'clsx';
import { useSelector } from 'react-redux';
import { selectors } from '@nike/ciclp-redux-app';
import {
  AccordionStyled,
  AccordionPanelStyled,
} from '@nike/nike-design-system-components';
import styles from './faq.styl';
import { BodyText, TitleText } from '../../components/textOverlay';
import SectionHeadline from '../../components/sectionHeadline';
import {
  sectionHeadlineShape,
  cardStyledTextProperties,
  textLocationShape,
} from '../../shapes';

const getHorizontalAlignmentClassName = ({ horizontal } = {}) =>
  styles[`h_${horizontal}`];

const isItemScope = (id, firstFAQId) => firstFAQId === id;

export const getItem = (id, firstFAQId, type, value) => {
  if (!isItemScope(id, firstFAQId)) return false;

  if (type === 'prop') return value;
  if (type === 'type') return `https://schema.org/${value}`;

  return false;
};

export const FAQ = ({
  slides,
  id,
  analyticsItemId,
  sectionHeadline,
  textLocation,
}) => {
  const firstFAQId = useSelector(state =>
    selectors.getFirstFAQCardIdSelector(state),
  );
  const hasNoSlides = !slides?.length;
  if (hasNoSlides) {
    return null;
  }

  return (
    <AccordionStyled
      id={id}
      className={cx([
        styles.faqAccordion,
        getHorizontalAlignmentClassName(textLocation),
      ])}
      itemScope={isItemScope(id, firstFAQId)}
      itemType={getItem(id, firstFAQId, 'type', 'FAQPage')}
    >
      {[
        ...(sectionHeadline?.title
          ? [
              <div
                key="faq-title"
                id={`${id}__title`}
                data-qa={`faq-title-${id}`}
              >
                <SectionHeadline
                  {...sectionHeadline}
                  analyticsItemId={`${analyticsItemId}_0`}
                  className={styles.faqHeadline}
                />
              </div>,
            ]
          : []),
        ...slides.map((slide, index) => (
          <div
            key={slide.id}
            data-analytics-placement-id={`${analyticsItemId}_${index + 1}`}
            data-analytics-has-landmark="true"
            data-analytics-allow="true"
            itemScope={isItemScope(id, firstFAQId)}
            itemProp={getItem(id, firstFAQId, 'prop', 'mainEntity')}
            itemType={getItem(id, firstFAQId, 'type', 'Question')}
          >
            <AccordionPanelStyled
              id={`${slide.id}__panel`}
              detailsTitle={
                <span itemProp="name">
                  <TitleText
                    {...slide.titleProps}
                    fontSize="small"
                    renderAs="span"
                    customClassName={styles.title}
                  />
                </span>
              }
              className={styles.faqSection}
              data-analytics-action-id={`${slide.id}_title`}
            >
              <div
                itemScope={isItemScope(id, firstFAQId)}
                itemProp={getItem(id, firstFAQId, 'prop', 'acceptedAnswer')}
                itemType={getItem(id, firstFAQId, 'type', 'Answer')}
                className={styles.faqBodyWrapper}
              >
                <BodyText
                  {...slide.bodyProps}
                  renderAs="div"
                  customClassName={styles.faqBody}
                  itemProp="text"
                />
              </div>
            </AccordionPanelStyled>
          </div>
        )),
      ]}
    </AccordionStyled>
  );
};

FAQ.propTypes = {
  analyticsItemId: PropTypes.string,
  id: PropTypes.string.isRequired,
  sectionHeadline: sectionHeadlineShape,
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      bodyProps: cardStyledTextProperties,
      id: PropTypes.string,
      titleProps: cardStyledTextProperties,
    }),
  ),
  textLocation: textLocationShape,
};
